// React
import { BaseSyntheticEvent, ChangeEvent, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { SnackbarKey, useSnackbar } from "notistack";

// MUI
import { Grid, Skeleton, Stack, Typography } from "@mui/material";

// API
import { getOemMetaMetrics, uploadFinancialStatement } from "@DataPages/api/oem";
import { OEMMetaMetric } from "@DataPages/api/oem/queries";

// Custom hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Helpers
import { getOEMShortNames, getSanitatedLocationOEMs } from "@/Common/helpers/OEMs";
import { getBucketIngestionFileNameKeys, getTabsShortNames } from "@Common/helpers/OEMs";
import { errorLogger } from "@Common/helpers/logger";

// Components
import TabSelector from "@Layouts/components/Tab/TabSelector";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import UploadDataWithTimerButton from "@Layouts/components/Buttons/UploadDataWithTimerButton";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import { SnackbarsActions, SnackbarsContent } from "@Layouts/components/Snackbars";
import OemMultiAdvancedSelect from "@DataPages/layouts/oem/OemMultiAdvancedSelect";
import OEMTabBody from "@DataPages/components/OEM/OEMTabBody";

// Constants
import actionsList from "@Constants/actionsList";
import { or } from "@Common/helpers/sonar";

// Types
import { RootState } from "@/redux/store";
import { ILocation, IOEM } from "@Common/interfaces/location";
import { ICommissionCycle } from "@Common/api/types/CommissionCycle";
import { Nullable } from "@Common/interfaces/types";

/*********************************************** HELPERS ***********************************************/
const endsWithData = (str: string) => {
	if (str) {
		const lowerCaseStr = str.toLowerCase();
		const lowerCaseWord = " data";
		return lowerCaseStr.endsWith(lowerCaseWord);
	}
	return str;
};

type TGetOEMOptionsArgs = Readonly<{
	location: Nullable<ILocation>;
	metaMetrics: OEMMetaMetric[];
	programsDepartments: { [key: string]: string[] };
	generalTabs: string[];
	indexTab: number;
}>;
const getOEMOptions = ({ location, metaMetrics, programsDepartments, generalTabs, indexTab }: TGetOEMOptionsArgs) => {
	if (!location) {
		return [];
	}

	const currentTab = generalTabs[indexTab];
	const locationOEMs = getSanitatedLocationOEMs(location?.oems || []) as (IOEM & { oemDisplayName: string })[];
	const options = locationOEMs?.map((oem, i) => {
		const fanchisesByOem = metaMetrics.find((item) => item.oemName === oem.oemName)?.franchisesByProgramByOem;
		const fanchisesByProgram = fanchisesByOem?.find((item) => item.oemDisplayName === oem.oemDisplayName)?.franchisesByProgram;
		const franchisesInSelectedProgram = fanchisesByProgram?.find((item) => item.program === currentTab);

		let franchises: {[label: string]: string}[] = [];
		if (franchisesInSelectedProgram?.franchises?.length) {
			franchises = franchisesInSelectedProgram.franchises.map((item) => ({
				label: item,
			}));
		} else {
			franchises = oem.franchises?.map((item) => ({
				label: item.franchise,
			}));
		}
		const positions = programsDepartments?.[`${oem.oemReference}_${currentTab}`] || "";
		return {
			positions,
			franchises,
			hideSubmenu: franchisesInSelectedProgram?.hideSubmenu || false,
			id: i,
			shortName: oem.oemReference,
			oemName: oem.oemName,
			dealerCodeFS: oem.dealerCodeFS,
			label: oem.oemDisplayName,
		};
	});

	const metrics = metaMetrics.filter((metaMetric) => metaMetric.metrics.includes(currentTab));

	return (
		metrics.reduce<OEMMetaMetric[]>((acc, metric) => {
			const localOptions = options.filter((option) => option.oemName === metric.oemName) as unknown as OEMMetaMetric[];
			return [...acc, ...localOptions];
		}, []) || []
	);
};

/********************************************* TAB HELPERS ***********************************************/
// Tabs in the order we want them to be shown, any tabs not listed here will be added at the end
const TABS_IN_ORDER = [
	"Financial Statement",
	"National Rankings",
	"Market Share",
	"Training",
	"CSI",
	"SSI",
	'PASE',
	"EBE",
	'Pinnacle',
	"SFE"
];

const extractTabNames = (oemMetaMetrics: OEMMetaMetric[]|null|undefined): string[] => {
	return oemMetaMetrics ? Array.from(new Set((oemMetaMetrics ?? []).flatMap((item) => item.metrics))) : [];
}

const getTabsInOrder = (metricTabs: string[]): string[] => {
	return metricTabs ? TABS_IN_ORDER.filter(tab => metricTabs.map(metricTab => metricTab.toUpperCase()).includes(tab.toUpperCase())) : [];
}

const addNewTabs = (metricTabs: string[]): string[] => {
	return metricTabs ? metricTabs.filter(tab => ! TABS_IN_ORDER.map(tabInOrder => tabInOrder.toUpperCase()).includes(tab.toUpperCase())) : [];
}

// Types
type IngestionOption = {
	oem: string;
	franchise?: string;
	dealerCodeFS: string;
	oemReference: string;
};

type OEMDataBodyProps = Readonly<{
	selected: ICommissionCycle | null;
}>;

type AdvancedSyntheticEvent = BaseSyntheticEvent<unknown[]> & ChangeEvent<HTMLBaseElement>;

// Constants
const FINANCIAL_STATEMENTS = "Financial Statements";
const NATIONAL_RANKINGS = "National Rankings";
const MARKET_SHARE = "Market Share";
const CSI = "CSI";

export default function OEMDataBody(props: OEMDataBodyProps) {
	const queryClient = useQueryClient();
	const { enqueueSnackbar } = useSnackbar();
	const { getSubscription } = useSubscriptions();

	const selectedCommissionCycleId = props.selected?._id;

	// Global State
	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId;

	// Hooks
	const oemDataPageSubscription = getSubscription(["oemDataPage", props.selected?._id]);

	// Permissions
	const { useAuth } = useAuthorization();
	const UPLOAD_OEM_REPORT = useAuth(actionsList.UPLOAD_OEM_REPORT);
	const VIEW_OEM_FINANCIAL_STATEMENTS = useAuth(actionsList.VIEW_OEM_FINANCIAL_STATEMENTS);
	const isUpdatable = or(props?.selected?.status === "CLOSING", props?.selected?.status === "ACTIVE");
	const oemMap = getOEMShortNames(location?.oems);
	const oneStepSelect = ["Financial Statement"];

	// States
	const [indexTab, setIndexTab] = useState(0);
	const [oem, setOem] = useState("");
	const [ingesting, setIngesting] = useState<string[]>([]);
	const [uploading, setUploading] = useState(false);
	const [ingestionOptions, setIngestionOptions] = useState<IngestionOption | null>(null);

	type OEMPageRelevantDetails = {
		generalTabs: string[];
		disabledMarketShares: string[];
		metaMetrics: OEMMetaMetric[];
		programsDepartments: { [key: string]: string[] };
	};

	/*********************************************** QUERIES ***********************************************/
	const {
		data: { generalTabs, disabledMarketShares, metaMetrics, programsDepartments } = {
			generalTabs: [],
			disabledMarketShares: [],
			metaMetrics: [],
			programsDepartments: {},
		},
		isLoading,
	} = useQuery<OEMPageRelevantDetails>({
		queryKey: ["oemGeneralTabs", locationId, selectedCommissionCycleId],
		queryFn: async (): Promise<OEMPageRelevantDetails> => {
			const oemMetaMetrics = await getOemMetaMetrics(locationId as string, selectedCommissionCycleId as string);

			const departments = oemMetaMetrics?.reduce((acc, curr) => {
				curr.programsDepartments.forEach((item) => {
					acc[item.name] = item.departments;
				});
				return acc;
			}, {} as { [key: string]: string[] });

			const metricTabs = extractTabNames(oemMetaMetrics);
			return {
				generalTabs: getTabsInOrder(metricTabs).concat(addNewTabs(metricTabs)),
				disabledMarketShares: (oemMetaMetrics ?? []).flatMap((item) => item.disabledMarketShares ?? []).filter(Boolean),
				metaMetrics: oemMetaMetrics ?? [],
				programsDepartments: departments ?? {},
			};
		},
		placeholderData: {
			generalTabs: [],
			disabledMarketShares: [],
			metaMetrics: [],
			programsDepartments: {},
		},
		enabled: Boolean(locationId) && Boolean(selectedCommissionCycleId),
		staleTime: Infinity,
	});

	/*********************************************** SUBSCRIPTIONS ***********************************************/
	const subscribeCallback = (subscriptionName: string, oemDataType: string) => {
		try {
			oemDataPageSubscription?.subscriptions?.[subscriptionName].setCallback(() => {
				queryClient.invalidateQueries({
					queryKey: ["oemData", location?.locationId, props.selected?._id, oemDataType],
				});
			});
		} catch (error) {
			errorLogger(error as Error, { function: "subscribeCallback", message: "Error subscribing to callback" });
		}
	};

	// Subscriptions
	// General Motors Financial Statement
	subscribeCallback("onUpdateGeneralMotorsFinancialStatement", FINANCIAL_STATEMENTS);

	// Mitsubishi Financial Statement
	subscribeCallback("onUpdateMitsubishiFinancialStatement", FINANCIAL_STATEMENTS);

	// Volvo Financial Statement
	subscribeCallback("onUpdateVolvoFinancialStatement", FINANCIAL_STATEMENTS);

	// Stellantis Financial Statement
	subscribeCallback("onUpdateStellantisFinancialStatement", FINANCIAL_STATEMENTS);

	// GeneralMotors National Ranking
	subscribeCallback("onUpdateGeneralMotorsNationalRanking", NATIONAL_RANKINGS);

	// Mitsubishi National Ranking
	subscribeCallback("onUpdateMitsubishiNationalRanking", NATIONAL_RANKINGS);

	// Volvo National Ranking
	subscribeCallback("onUpdateVolvoNationalRanking", NATIONAL_RANKINGS);

	// Stellantis National Ranking
	subscribeCallback("onUpdateStellantisNationalRanking", NATIONAL_RANKINGS);

	// GeneralMotors Market Share
	subscribeCallback("onUpdateGeneralMotorsMarketShare", MARKET_SHARE);

	// Mitsubishi Market Share
	subscribeCallback("onUpdateMitsubishiMarketShare", MARKET_SHARE);

	// Stellantis Market Share
	subscribeCallback("onUpdateStellantisMarketShare", MARKET_SHARE);

	// Volvo Market Share
	subscribeCallback("onUpdateVolvoMarketShare", MARKET_SHARE);

	// GeneralMotors CSI
	subscribeCallback("onUpdateGeneralMotorsCSISingleMetric", CSI);

	// Mitsubishi CSI
	subscribeCallback("onUpdateMitsubishiCSISingleMetric", CSI);

	// Volvo CSI
	subscribeCallback("onUpdateVolvoCSISingleMetric", CSI);

	// Stellantis CSI
	subscribeCallback("onUpdateStellantisCSISingleMetric", CSI);

	// Mitsubishi SSI
	subscribeCallback("onUpdateMitsubishiSSISingleMetric", "SSI");

	// Stellantis SSI
	subscribeCallback("onUpdateStellantisSSISingleMetric", "SSI");

	// GeneralMotors EBE
	subscribeCallback("onUpdateGeneralMotorsEBE", "EBE");

	// GeneralMotors SFE
	subscribeCallback("onUpdateGeneralMotorsSFE", "SFE");

	// GeneralMotors PASE
	subscribeCallback("onUpdateGeneralMotorsPASE", "PASE");

	// GeneralMotors Pinnacle
	subscribeCallback("onUpdateGeneralMotorsPinnacle", "Pinnacle");

	/*********************************************** HELPERS ***********************************************/
	const getBucketIngestionName = () => {
		const tabNameMap = getTabsShortNames(generalTabs);
		const tabShortName = tabNameMap?.get(generalTabs[indexTab]);
		const oemShortName = oemMap.get(ingestionOptions?.oem ?? "");
		if (!tabShortName || !oemShortName) {
			throw new Error(`No bucket short name found for ${generalTabs[indexTab] || ""}`);
		}
		return `${oemShortName}_${tabShortName}_INGESTION_BUCKET`;
	};

	const getSelectedFranchiseName = () => ingestionOptions?.franchise ?? "";

	// Define the action component outside to prevent re-creation
	const renderSnackbarsActions = (key: SnackbarKey) => {
		return <SnackbarsActions key={key} index={key} />;
	};

	const showSnackBar = (dataObject: { message: string, critical: boolean|undefined }) => {
		enqueueSnackbar(<SnackbarsContent message={dataObject?.message} critical={dataObject?.critical} />, {
			variant: "error",
			preventDuplicate: true,
			action: renderSnackbarsActions,
		});
	};

	const formattedOemNames = metaMetrics.filter((item) => item.metrics.includes(generalTabs[indexTab]));
	const stringifiedFormattedOemNames = JSON.stringify(formattedOemNames);

	/*********************************************** HANDLERS ***********************************************/
	const handleTabOnChange = (index: number) => {
		setIndexTab(index);
		setIngestionOptions(null);
	};

	const handleUploadFileTimer = (value = false) => setUploading(value);

	const handleUploadFile = async (event: AdvancedSyntheticEvent) => {
		const file = event?.target?.files[0] || null;

		try {
			if (
				file?.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
				file?.type === "application/vnd.ms-excel"
			) {
				if (!locationId || !selectedCommissionCycleId) {
					event.target.value = null;
					return;
				}

				const updateIngesting = [...ingesting];
				updateIngesting.push(generalTabs[indexTab]);
				handleUploadFileTimer(true);
				setIngesting(updateIngesting);
				const tabsNameMap = getTabsShortNames(generalTabs);
				const options = {
					bucketEnvVariable: getBucketIngestionName(),
					action: "putObject",
					contentType: file?.type,
					filePathKeys: ["tenant", "location", "dealerCodeFS", "commissionCycleId"],
					fileNameKeys: getBucketIngestionFileNameKeys(tabsNameMap, oneStepSelect, generalTabs[indexTab]),
					franchise: getSelectedFranchiseName(),
					dealerCodeFS: ingestionOptions?.dealerCodeFS,
					oemReference: ingestionOptions?.oemReference,
				};

				const uploaded = await uploadFinancialStatement({
					locationId: location.locationId,
					commissionCycleId: props.selected._id,
					file,
					options,
				});

				if (uploaded) {
					handleUploadFileTimer();
					event.target.value = null;
				}

				if (updateIngesting?.includes(generalTabs[indexTab])) {
					setIngesting(updateIngesting?.filter((item) => item !== generalTabs[indexTab]));
				}
			}
		} catch (error) {
			showSnackBar({ message: (error as Error).message, critical: true});
			handleUploadFileTimer();
			errorLogger(error as Error, { function: "handleUploadFile", message: "Error uploading file" });
		}
	};

	const handleSetOptionsCallback = (ingestionObj: IngestionOption) => {
		setTimeout(() => setIngestionOptions(ingestionObj));
	};
	const handleOnClearCallback = () => setIngestionOptions(null);
	const handleOnRemoveCallback = () => setIngestionOptions(null);

	/*********************************************** EFFECTS ***********************************************/
	useEffect(() => {
		if (formattedOemNames?.length) {
			if (stringifiedFormattedOemNames.includes(oem)) {
				setOem((p) => p.toString());
			} else {
				setOem(formattedOemNames[0]?.oemName);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stringifiedFormattedOemNames]);

	useEffect(() => {
		setIndexTab(VIEW_OEM_FINANCIAL_STATEMENTS ? 0 : 1);
		return () => {
			setIndexTab(0);
		};
	}, [locationId, VIEW_OEM_FINANCIAL_STATEMENTS]);

	if (or(isLoading, !props.selected, !generalTabs?.length, !metaMetrics?.length, !programsDepartments, !location)) {
		return (
			<RoundedBox component={"section"} className={"!px-0 !h-[630px]"}>
				<TabLoader tabs={5} />
				<Stack
					direction="row"
					justifyContent={"space-evenly"}
					alignItems={"center"}
					id={"options"}
					marginRight={4}
					marginTop={2}
					paddingLeft={4}
				>
					<Skeleton variant="rectangular" width={400} height={30} className={"rounded-[6px]"} />
					<Grid container justifyContent="flex-end" display="flex" flexDirection="row" sx={{ padding: "40px 5px" }}>
						<Skeleton variant="rectangular" height={30} width={202} sx={{ borderRadius: "6px", marginRight: "32px" }} />
						<Skeleton variant="rectangular" height={30} width={180} sx={{ borderRadius: "6px", marginRight: "23px" }} />
					</Grid>
				</Stack>
				<Stack mt={0} mb={2} sx={{ padding: "15px 30px" }}>
					<Skeleton variant="rectangular" width={280} height={25} sx={{ borderRadius: "6px" }} />
					<Skeleton variant="rectangular" width={100} height={20} sx={{ borderRadius: "6px", marginTop: "20px" }} />
					<TableLoader content rows={7} />
				</Stack>
			</RoundedBox>
		);
	}

	const oemMetricOptions = getOEMOptions({ location, metaMetrics, programsDepartments, generalTabs, indexTab });
	const isDisabled = or(!UPLOAD_OEM_REPORT, ingesting?.includes(generalTabs[indexTab]), !isUpdatable, !ingestionOptions);
	const buttonClassName = isDisabled
		? "upload-file-btn btn-rounded !bg-disabled !text-white"
		: "upload-file-btn medium-blue-btn btn-rounded chargebacks-upload-btn";

	return (
		<RoundedBox component={"section"} className={"!py-[8px] !px-0 !min-w-[630px]"}>
			<TabSelector
				defaultTab={indexTab}
				readOnly={false}
				data={{
					tabs: generalTabs,
					disabledTab: [!VIEW_OEM_FINANCIAL_STATEMENTS],
					topSwShow: false,
					topSwitchAdditional: false,
					bottomSwShow: false,
				}}
				contentProps={{ style: { marginTop: "62px" } }}
				tabValue={handleTabOnChange}
			/>
			<Stack direction="row" justifyContent={"space-between"} alignItems={"center"} id={"options"} marginRight={4} marginTop={4}>
				<Typography className={"page-title px-[32px]"} sx={{ lineHeight: "1.2" }}>{`${props.selected?.title} ${
					generalTabs[indexTab]
				} ${endsWithData(generalTabs[indexTab]) ? "" : "Data"}`}</Typography>
				<Stack direction="row" spacing={3}>
					<OemMultiAdvancedSelect
						sx={{ width: "270px" }}
						options={oemMetricOptions}
						setOptionsCallBack={handleSetOptionsCallback}
						hideSubmenu={oneStepSelect.includes(generalTabs[indexTab])}
						chipWidth={oneStepSelect.includes(generalTabs[indexTab])}
						onClear={handleOnClearCallback}
						onRemoveOption={handleOnRemoveCallback}
						placeholder={oneStepSelect.includes(generalTabs[indexTab]) ? "Select OEM" : "Select OEM & Franchise"}
						disabledMarketShares={disabledMarketShares}
						tab={generalTabs[indexTab]}
						errorMessages={{
							"Market Share": "Market Share imported with National Ranking",
						}}
					/>
					<UploadDataWithTimerButton
						title={`Import ${generalTabs[indexTab]}`}
						className={buttonClassName}
						accept=".xlsx, .csv, .xls"
						disabled={isDisabled}
						uploading={uploading}
						onChange={handleUploadFile}
						sx={{ padding: "6px 18px", minWidth: "180px", width: "170px" }}
					/>
				</Stack>
			</Stack>

			<OEMTabBody selected={props.selected as ICommissionCycle} tab={generalTabs[indexTab]} location={location as ILocation} />
		</RoundedBox>
	);
}
